import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { resizeImage } from '../utils/imageResize';

const DataContext = createContext();
export const useData = () => useContext(DataContext);

// ── Supabase client ──
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
export const supabase = SUPABASE_URL ? createClient(SUPABASE_URL, SUPABASE_ANON) : null;

// ── Defaults ──
const defaultArtists = [
  { id: 1, name: 'Artist Ekle', style: 'Fine Line · Blackwork', bio: 'Detaylı fine line ve blackwork dövmelerinde uzmanlaşmış artist.', instagram: '@artist1', available: true, photo: null, tags: ['Fine Line', 'Blackwork', 'Minimalist'] },
  { id: 2, name: 'Artist Ekle', style: 'Neo Traditional · Color', bio: 'Renkli ve neo traditional stilinde özgün çalışmalar.', instagram: '@artist2', available: true, photo: null, tags: ['Neo Traditional', 'Color', 'Illustrative'] },
  { id: 3, name: 'Artist Ekle', style: 'Dotwork · Geometric', bio: 'Geometrik desenler ve dotwork tekniğinde uzman.', instagram: '@artist3', available: false, photo: null, tags: ['Dotwork', 'Geometric', 'Sacred Geometry'] },
];

const defaultGallery = [
  { id: 1, url: null, title: 'Çalışma 1', artist: 'Artist 1', artistId: 1, style: 'Fine Line', featured: true },
  { id: 2, url: null, title: 'Çalışma 2', artist: 'Artist 2', artistId: 2, style: 'Neo Traditional', featured: false },
  { id: 3, url: null, title: 'Çalışma 3', artist: 'Artist 3', artistId: 3, style: 'Dotwork', featured: false },
  { id: 4, url: null, title: 'Çalışma 4', artist: 'Artist 1', artistId: 1, style: 'Blackwork', featured: true },
  { id: 5, url: null, title: 'Çalışma 5', artist: 'Artist 2', artistId: 2, style: 'Color', featured: false },
  { id: 6, url: null, title: 'Çalışma 6', artist: 'Artist 3', artistId: 3, style: 'Geometric', featured: false },
];

const defaultEvents = [
  { id: 1, title: 'Tattoo Design Workshop #01', type: 'WORKSHOP', description: 'Kendi dövme tasarımını kendin yap.', date: '2024-03-15', time: '10:00 — 17:00', location: 'X HAUS Studio, Eskişehir', price: '₺500 / Kişi · Malzeme Dahil', capacity: 12, registered: 8, featured: true, active: true, ctaLabel: 'Kayıt Ol', image: null },
  { id: 2, title: 'Bahar Kampanyası', type: 'ÖZEL GÜN', description: 'Seçili tasarımlarda %20 indirim.', date: '2024-03-22', time: 'Tüm Gün', location: 'Stüdyo', price: 'Ücretsiz Katılım', capacity: null, registered: 0, featured: false, active: true, ctaLabel: 'Randevu Al', image: null },
  { id: 3, title: 'Dotwork Masterclass', type: 'WORKSHOP', description: 'Nokta tekniğinin ustasından birebir eğitim.', date: '2024-04-05', time: '13:00 — 19:00', location: 'X HAUS Studio', price: '₺350 / Kişi', capacity: 6, registered: 2, featured: false, active: true, ctaLabel: 'Kayıt Ol', image: null },
  { id: 4, title: 'Eskişehir Tattoo Fest', type: 'CONVENTION', description: 'X HAUS olarak katılacağımız yıllık dövme festivali.', date: '2024-04-19', time: '2 Gün', location: 'Congress Center', price: 'Ücretsiz Giriş', capacity: null, registered: 0, featured: false, active: true, ctaLabel: 'Detaylar', image: null },
];

const defaultPastEvents = [
  { id: 101, title: 'Workshop #00', date: 'Şubat 2024' },
  { id: 102, title: 'Fine Line Masterclass', date: 'Aralık 2023' },
  { id: 103, title: 'Yılbaşı Özel', date: 'Aralık 2023' },
  { id: 104, title: 'İlk Dövme Kampanyası', date: 'Ekim 2023' },
];

const defaultSettings = {
  studioName: 'X HAUS',
  tagline: 'Tatto Art Studio',
  address: 'Hoşnudiye Mah. Şahin Bulvarı. Zeydanlar Location No:17 / B Blok Kat:2 D:2 Tepebaşı / ESKİŞEHİR',
  email: 'info@xhaus.com',
  instagram: '@xhaus.tattoo',
  phone: '+90 000 000 0000',
  hours: 'Pazartesi — Cumartesi: 11:00 — 20:00',
  // Hero
  heroTag: '— Eskişehir / Est. 2024',
  heroTitle: 'X HAUS',
  heroSubtitle: 'Tatto · Art · Studio',
  heroCtaText: 'Randevu Al',
  heroCtaSecondary: 'Galeriyi Gör',
  heroImage: null,
  heroVideo: null,
  heroMediaType: 'video', // 'image' veya 'video'
  // Marquee
  marqueeItems: ['ART YOU CARRY', 'X HAUS', 'HOUSE OF LINE', 'CRAFTED WITH ART', 'X HAUS RULES', 'ESKİŞEHİR'],
  // Brand grid images (null = default asset kullan)
  brandImages: [null, null, null, null, null],
  brandRedTitle: 'X\nHAUS',
  brandRedSub: 'Art You Carry',
  // About
  aboutHeading: 'HOUSE OF LINE.',
  aboutText: 'X HAUS, sanatı vücudunuza işleme tutkusuyla Eskişehir\'de kurulmuş bir dövme sanat stüdyosudur. Her çizgi, her nokta ve her gölge — ustalıkla, titizlikle ve sizi yansıtacak şekilde işlenir.',
  aboutQuote: 'Art You Carry.',
  stats: [
    { num: '5+', label: 'Uzman Artist' },
    { num: '500+', label: 'Tamamlanan İş' },
    { num: '3', label: 'Yıllık Deneyim' },
    { num: '∞', label: 'Benzersiz Tasarım' },
  ],
  // Artists section
  artistsSectionLabel: 'Artistlerimiz',
  artistsSectionHeading: 'EKİBİMİZ',
  // Gallery section
  gallerySectionLabel: 'Galeri',
  gallerySectionHeading: 'ÇALIŞMALARIMIZ',
  // Events section
  eventsSectionLabel: 'Etkinlikler',
  eventsSectionHeading: 'YAKLAŞAN ETKİNLİKLER',
  eventsDesc: 'Workshop\'lar, özel günler ve stüdyo etkinlikleri. Kaçırmak istemediğin anlara buradan ulaş.',
  // Contact section
  contactSectionLabel: 'İletişim',
  contactSectionHeading: 'RANDEVU AL.',
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.3!2d30.5167!3d39.7769!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cc1bae38c5b64f%3A0xd95e21d1d3b05c!2sTepebaşı%2C+Eskişehir!5e0!3m2!1str!2str!4v1708000000000!5m2!1str!2str',
  mapLink: 'https://maps.google.com/?q=Hoşnudiye+Mah.+Şahin+Bulvarı+Tepebaşı+Eskişehir',
  mapPinLabel: 'Konumumuz',
  mapPinAddr: 'Tepebaşı / ESKİŞEHİR',
  // Footer
  footerCopy: '© 2024 X HAUS TATTO ART STUDIO — ESKİŞEHİR',
  footerSlogan: 'CRAFTED WITH ART',
  // Email (EmailJS)
  emailServiceId: '',
  emailTemplateContact: '',
  emailTemplateBooking: '',
  emailPublicKey: '',
  notificationEmail: 'info@xhaus.com',
};

// ── Helper: upload video to Supabase Storage ──
export async function uploadVideo(file, bucket = 'images') {
  if (!supabase) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(file);
    });
  }
  const ext = file.name.split('.').pop();
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from(bucket).upload(fileName, file);
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
  return data.publicUrl;
}

// ── Helper: upload image to Supabase Storage (with auto-resize) ──
export async function uploadImage(file, bucket = 'images') {
  // Otomatik boyutlandırma & sıkıştırma (max 1920px, webp)
  let processed = file;
  try {
    processed = await resizeImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.85 });
  } catch (e) {
    console.warn('Image resize failed, using original:', e);
  }

  if (!supabase) {
    // Fallback: base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(processed);
    });
  }
  const ext = processed.name.split('.').pop();
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from(bucket).upload(fileName, processed);
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
  return data.publicUrl;
}

// ── Provider ──
export const DataProvider = ({ children }) => {
  const [artists, setArtists] = useState(defaultArtists);
  const [gallery, setGallery] = useState(defaultGallery);
  const [events, setEvents] = useState(defaultEvents);
  const [pastEvents, setPastEvents] = useState(defaultPastEvents);
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  // ── Load from Supabase on mount ──
  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    (async () => {
      try {
        const [a, g, e, pe, s] = await Promise.all([
          supabase.from('artists').select('*').order('id'),
          supabase.from('gallery').select('*').order('id'),
          supabase.from('events').select('*').order('id'),
          supabase.from('past_events').select('*').order('id'),
          supabase.from('settings').select('*').single(),
        ]);
        if (a.data?.length) setArtists(a.data.map(r => ({ ...r, tags: r.tags || [] })));
        if (g.data?.length) setGallery(g.data);
        if (e.data?.length) setEvents(e.data);
        if (pe.data?.length) setPastEvents(pe.data);
        if (s.data) {
          const { id, created_at, ...rest } = s.data;
          setSettings(prev => ({ ...prev, ...rest }));
        }
      } catch (err) {
        console.error('Supabase load error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Supabase sync helpers ──
  const dbUpsert = useCallback(async (table, data) => {
    if (!supabase) return;
    try { await supabase.from(table).upsert(data); } catch (e) { console.error(e); }
  }, []);
  const dbDelete = useCallback(async (table, id) => {
    if (!supabase) return;
    try { await supabase.from(table).delete().eq('id', id); } catch (e) { console.error(e); }
  }, []);

  // ── Artist CRUD ──
  const addArtist = (artist) => {
    const newA = { ...artist, id: Date.now() };
    setArtists(prev => [...prev, newA]);
    dbUpsert('artists', newA);
  };
  const updateArtist = (id, data) => {
    setArtists(prev => prev.map(a => {
      if (a.id !== id) return a;
      const updated = { ...a, ...data };
      dbUpsert('artists', updated);
      return updated;
    }));
  };
  const deleteArtist = (id) => {
    setArtists(prev => prev.filter(a => a.id !== id));
    dbDelete('artists', id);
  };

  // ── Gallery CRUD ──
  const addPhoto = (photo) => {
    const newP = { ...photo, id: Date.now() };
    setGallery(prev => [...prev, newP]);
    dbUpsert('gallery', newP);
  };
  const updatePhoto = (id, data) => {
    setGallery(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, ...data };
      dbUpsert('gallery', updated);
      return updated;
    }));
  };
  const deletePhoto = (id) => {
    setGallery(prev => prev.filter(p => p.id !== id));
    dbDelete('gallery', id);
  };

  // ── Events CRUD ──
  const addEvent = (event) => {
    const newE = { ...event, id: Date.now() };
    setEvents(prev => [...prev, newE]);
    dbUpsert('events', newE);
  };
  const updateEvent = (id, data) => {
    setEvents(prev => prev.map(e => {
      if (e.id !== id) return e;
      const updated = { ...e, ...data };
      dbUpsert('events', updated);
      return updated;
    }));
  };
  const deleteEvent = (id) => {
    setEvents(prev => prev.filter(e => e.id !== id));
    dbDelete('events', id);
  };
  const setFeaturedEvent = (id) => {
    setEvents(prev => {
      const updated = prev.map(e => ({ ...e, featured: e.id === id }));
      if (supabase) updated.forEach(e => dbUpsert('events', e));
      return updated;
    });
  };

  const addPastEvent = (pe) => {
    const newPE = { ...pe, id: Date.now() };
    setPastEvents(prev => [...prev, newPE]);
    dbUpsert('past_events', newPE);
  };
  const deletePastEvent = (id) => {
    setPastEvents(prev => prev.filter(e => e.id !== id));
    dbDelete('past_events', id);
  };

  // ── Settings ──
  const updateSettings = (data) => {
    setSettings(prev => {
      const updated = { ...prev, ...data };
      if (supabase) {
        const toSave = { id: 1, ...updated };
        dbUpsert('settings', toSave);
      }
      return updated;
    });
  };

  return (
    <DataContext.Provider value={{
      artists, addArtist, updateArtist, deleteArtist,
      gallery, addPhoto, updatePhoto, deletePhoto,
      events, addEvent, updateEvent, deleteEvent, setFeaturedEvent,
      pastEvents, addPastEvent, deletePastEvent,
      settings, updateSettings,
      loading, supabase,
      uploadImage,
    }}>
      {children}
    </DataContext.Provider>
  );
};
